/**
 * Установка программы на телефон или компьютер.
 *
 * Заменяет собой отдельную сборку под рабочий стол: браузер ставит сайт как
 * приложение — со своим значком, отдельным окном без адресной строки и
 * запуском сразу в заказы. Обновляется вместе с сайтом, переустанавливать
 * ничего не нужно.
 *
 * Способ зависит от браузера, и это не прихоть оформления:
 *
 *   - Chrome, Edge, Android — умеют показать приглашение сами. Браузер
 *     заранее сообщает об этом событием, его надо ПЕРЕХВАТИТЬ и придержать:
 *     иначе приглашение мелькнёт в углу и пропадёт, а вызвать его потом
 *     будет нечем.
 *   - iPhone — такого события нет вовсе, Safari ставит только вручную через
 *     «Поделиться». Показываем, куда нажимать.
 *   - Firefox — не умеет; честнее сказать прямо, чем показывать кнопку,
 *     которая ничего не сделает.
 */

import { useEffect, useState } from 'react';
import { Check, Share, Smartphone } from 'lucide-react';
import { Button } from './ui.js';

/**
 * Событие приглашения. В типах браузера его нет: оно нестандартное, хотя
 * и поддержано всеми, кроме Safari и Firefox.
 */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** Программа уже открыта как приложение, а не во вкладке. */
function isInstalled(): boolean {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      // Safari на iPhone своего медиа-признака не выставляет.
      (window.navigator as { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function InstallApp() {
  const [prompt, setPrompt] = useState<InstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isInstalled);

  useEffect(() => {
    const onPrompt = (event: Event) => {
      // Без этого браузер покажет своё приглашение и забудет о нём.
      event.preventDefault();
      setPrompt(event as InstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!prompt) return;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    // Приглашение одноразовое: показать его второй раз браузер не даст.
    setPrompt(null);
    if (outcome === 'accepted') setInstalled(true);
  };

  return (
    <section
      className="mb-6 rounded-lg border p-4"
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
    >
      <p className="mb-1 flex items-center gap-2 text-sm font-medium text-ink">
        <Smartphone size={16} /> Приложение на телефон и компьютер
      </p>


      {installed ? (
        <p className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--otc-accent)' }}>
          <Check size={15} /> Уже установлено
        </p>
      ) : prompt ? (
        <Button onClick={() => void install()}>Установить приложение</Button>
      ) : isIos() ? (
        <p className="flex items-start gap-1.5 text-sm text-ink">
          <Share size={15} className="mt-0.5 flex-shrink-0" />
          <span>
            В Safari нажмите «Поделиться», затем <b>«На экран „Домой“»</b>. Кнопки установки
            у Apple нет — только так.
          </span>
        </p>
      ) : (
        <p className="text-sm text-ink2">
          Откройте меню браузера и выберите <b>«Установить приложение»</b>. Так умеют Chrome и
          Edge на любом устройстве; Firefox устанавливать сайты не умеет.
        </p>
      )}
    </section>
  );
}
